import { AcceptEulaPrompt } from '@features/operations/routes/AcceptEulaPrompt'
import { EulaStatus, getEulaStatus } from 'api-validations/user-api'
import { useState } from 'react'
import { Link, Outlet } from 'react-router'
import { useAccount } from 'shared-ui/active-operation'
import { useAuth } from 'shared-ui/auth-provider'
import { ButtonKind, EulaIcon, Loading, Modal, PanoramaButton } from 'shared-ui/components'
import { Trans, useLocalization } from 'shared-ui/localization'
import { useAcceptEula } from 'shared-ui/util-hooks'

import { path } from '../../../routes/paths'

const UpdatedEulaModal: React.FC = () => {
  const { t } = useLocalization()
  const acceptEula = useAcceptEula()
  const account = useAccount()
  const { signOut } = useAuth()
  const [isChecked, setIsChecked] = useState(false)

  const handleContinue = () => {
    if (account.isSuccess) {
      acceptEula.mutate({ username: account.data.username })
    }
  }

  return (
    <Modal show unstyled size="full">
      <div className="max-w-[46rem] rounded-lg bg-white">
        <div className="flex w-full flex-col gap-8 p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <div className="flex size-8 items-center justify-center">
                <EulaIcon className="text-green-500" />
              </div>
              <h2 className="pan-title4 text-black">{t(($) => $.eula.updatedPrivacyTitle)}</h2>
            </div>

            <div className="mx-4 flex flex-col items-start gap-2">
              <h3 className="pan-title5 font-bold text-black">
                {t(($) => $.eula.summaryOfUpdates)}
              </h3>
              <ul className="ml-8 list-outside list-disc space-y-2 text-left">
                <li>{t(($) => $.eula.updateAuroraOption)}</li>
                <li>{t(($) => $.eula.updateDataRetention)}</li>
              </ul>
            </div>
          </div>

          <div className="pl-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="size-4 cursor-pointer text-white accent-black"
              />
              <span className="pan-body text-gray-700">
                <Trans
                  i18nKey={($) => $.eula.agreeToTerms}
                  components={[
                    <Link
                      key="terms"
                      to={path.publicEula}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 underline"
                    />
                  ]}
                />
              </span>
            </label>
          </div>

          <div className="flex justify-center gap-4">
            <PanoramaButton onClick={signOut} kind={ButtonKind.Secondary} className="w-full">
              {t(($) => $.buttons.declineAndLogOut)}
            </PanoramaButton>
            <PanoramaButton
              onClick={handleContinue}
              disabledWhen={{ when: !isChecked, reason: t(($) => $.eula.acceptTermsFirst) }}
              loading={acceptEula.isPending}
              className="w-full"
            >
              {acceptEula.isPending ? <Loading /> : 'Continue'}
            </PanoramaButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const RequireAcceptEulaGuard: React.FC = () => {
  const account = useAccount()

  const eulaStatus = getEulaStatus(account.data?.acceptedEulaDate)

  if (eulaStatus === EulaStatus.NeverAccepted) {
    return <AcceptEulaPrompt />
  }

  return (
    <>
      <Outlet />
      {eulaStatus === EulaStatus.NeedsUpdate && <UpdatedEulaModal />}
    </>
  )
}
